import { effect, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { clienteActivoIdSignal } from './client-context.state';
import { AppStateService } from './app-state.service';
import { alarmasPermitidasSignal } from '../../state/alarm-context.state';

/**
 * ════════════════════════════════════════════════════════════════
 * CAMBIO DE CLIENTE
 *
 * Al cambiar de cliente, todo lo que hay en pantalla pertenece al
 * cliente anterior. Muchas vistas están amarradas a una entidad
 * concreta de ese cliente (siteGroupId, meterId, concessionId) que
 * no tiene equivalente en el nuevo: no se pueden "recargar en su
 * sitio", hay que salir de ellas.
 *
 * Por eso la regla es única y central: se limpia el estado global
 * y se regresa al dashboard. Así ninguna vista puede quedarse
 * mostrando datos de un cliente que ya no es el seleccionado.
 *
 * Se activa desde LayoutComponent, igual que TooltipInitService.
 * ════════════════════════════════════════════════════════════════
 */
@Injectable({ providedIn: 'root' })
export class ClientSwitchService {

  private srvRouter = inject(Router);
  private appState = inject(AppStateService);

  /**
   * Último cliente visto. Sirve para distinguir la resolución inicial
   * del contexto (login / recarga de página) de un cambio real:
   * en el primer caso no hay nada que limpiar ni a dónde navegar.
   */
  private clienteAnterior: number | null = null;

  constructor() {
    effect(() => {
      const clienteId = clienteActivoIdSignal();

      if (clienteId === null) {
        this.clienteAnterior = null;
        return;
      }

      const previo = this.clienteAnterior;
      this.clienteAnterior = clienteId;

      // Primera resolución del contexto, no un cambio
      if (previo === null || previo === clienteId) return;

      this.limpiarContextoAnterior();

      // Si ya estamos en el dashboard, Angular ignora la navegación
      // y el effect del propio dashboard se encarga de recargar.
      this.srvRouter.navigateByUrl('/dashboard');
    });
  }

  /**
   * Estado global que pertenece al cliente anterior.
   * Se limpia antes de navegar para que no alcance a pintarse.
   */
  private limpiarContextoAnterior(): void {
    this.appState.clear();

    // null = "aún no cargadas": evita evaluar alarmas con el catálogo
    // del cliente anterior mientras llega el del nuevo.
    alarmasPermitidasSignal.set(null);
  }
}
